import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { ContractsService } from '../../services/contracts.service';
import { Contract, ContractFreezeResponse } from '../../models/Contract';
import { statusAllowsContractFreeze, statusAllowsContractUnfreeze } from '../../utils/contract-freeze.utils';
import { ErrorService } from '../../shared/services/error.service';
import { ConfirmationService } from '../../shared/services/confirmation.service';
import { TableAction } from '../../shared/components/table/table.component';
import { PaymentService } from '../../services/payment.service';
import {
  ContractCancelReason,
  CONTRACT_CANCEL_REASON_SELECT_OPTIONS,
  contractCancelMotivoFromCode
} from '../../constants/contract-cancel-reasons';

@Component({
  selector: 'app-contracts',
  templateUrl: './contracts.component.html',
  styleUrls: ['./contracts.component.css']
})
export class ContractsComponent implements OnInit, OnDestroy {
  contracts: Contract[] = [];
  isLoading = false;
  searchTerm = '';
  selectedStatus = '';

  currentPage = 1;
  pageSize = 10;
  totalCount = 0;
  totalPages = 1;

  /** Dados do dashboard (contagens por status); preenchido por loadDashboard(). */
  dashboard: { totalCount: number; countsByStatus: { status: string; count: number }[] } | null = null;

  showCancelModal = false;
  contractToCancel: Contract | null = null;
  /** Código do enum {@link ContractCancelReason}; obrigatório para confirmar cancelamento. */
  cancelReasonCode: ContractCancelReason | '' = '';
  readonly contractCancelReasonOptions = CONTRACT_CANCEL_REASON_SELECT_OPTIONS;

  showFreezeModal = false;
  contractFreezeId: number | null = null;
  contractFreezeSubtitle = '';

  tableColumns = [
    { titulo: 'Número', label: 'contractNumber' },
    { titulo: 'Motorista', label: 'driverName' },
    { titulo: 'CPF/CNPJ', label: 'driverCpfCnpj' },
    { titulo: 'Veículo', label: 'vehicleInfo' },
    { titulo: 'Valor', label: 'baseAmount' },
    { titulo: 'Período', label: 'period' },
    { titulo: 'Status', label: 'statusLabel' }
  ];

  visibleActions: TableAction[] = [
    { label: 'Visualizar', icon: 'fa-solid fa-eye', action: 'view', visible: true, class: 'btn-view' },
  ];

  /**
   * Menu ⋮: cada linha usa `_actions` (Congelar **ou** Descongelar conforme o status).
   * Fallback vazio — o dropdown vem de `transformContractsData`.
   */
  tableActions: TableAction[] = [];

  sortBy = '';
  sortOrder: 'asc' | 'desc' = 'asc';
  private searchDebounce: any = null;

  statusOptions = [
    { value: '', label: 'Todos os status' },
    { value: 'Pendente', label: 'Pendente' },
    { value: 'Assinado', label: 'Assinado' },
    { value: 'Vigente', label: 'Vigente' },
    { value: 'NearExpiration', label: 'Próximos ao Vencimento' },
    { value: 'Aguardando Assinatura', label: 'Aguardando Assinatura' },
    { value: 'Cancelado', label: 'Cancelado' },
    { value: 'Vencido', label: 'Vencido' }
  ];

  constructor(
    private router: Router,
    private contractsService: ContractsService,
    private messageService: ErrorService,
    private paymentService: PaymentService,
    private confirmationService: ConfirmationService
  ) {}

  ngOnInit(): void {
    this.loadDashboard();
    this.loadContracts();
  }

  ngOnDestroy(): void {
    document.body.classList.remove('modal-open');
  }

  @HostListener('document:keydown', ['$event'])
  onCancelModalDocumentKeydown(ev: KeyboardEvent): void {
    if (!this.showCancelModal || ev.key !== 'Escape' || this.isLoading) {
      return;
    }
    this.closeCancelModal();
  }

  onCancelModalBackdropClick(ev: MouseEvent): void {
    const t = ev.target as HTMLElement;
    if (t?.classList?.contains('ccm-backdrop') && !this.isLoading) {
      this.closeCancelModal();
    }
  }

  private setCancelModalOpen(open: boolean): void {
    this.showCancelModal = open;
    if (open) {
      document.body.classList.add('modal-open');
    } else {
      document.body.classList.remove('modal-open');
    }
  }

  /** Select de motivo preenchido e não em envio. */
  canSubmitContractCancel(): boolean {
    return !!this.cancelReasonCode && !this.isLoading;
  }

  loadDashboard(): void {
    this.contractsService.getContractsDashboard().subscribe({
      next: (res) => { this.dashboard = res; },
      error: () => { this.dashboard = null; }
    });
  }

  loadContracts(): void {
    this.isLoading = true;
    const filters: { filter?: string; status?: string } = {};
    if (this.searchTerm.trim()) filters.filter = this.searchTerm.trim();
    if (this.selectedStatus && this.selectedStatus !== 'NearExpiration') {
      filters.status = this.selectedStatus;
    }

    this.contractsService
      .listContractsPaginated(this.currentPage, this.pageSize, Object.keys(filters).length > 0 ? filters : undefined)
      .subscribe({
        next: (res) => {
          let list = res.contracts;
          if (this.selectedStatus === 'NearExpiration') {
            list = this.filterNearExpiration(list);
          }
          this.contracts = this.transformContractsData(list);
          this.totalCount = res.totalCount;
          this.totalPages = Math.max(1, res.totalPages);
          this.isLoading = false;
        },
        error: (err) => {
          this.messageService.showErrorMessage(`Erro ao carregar contratos: ${err.message}`);
          this.isLoading = false;
        }
      });
  }

  onPageChange(page: number): void {
    this.currentPage = page;
    this.loadContracts();
  }

  onPageSizeChange(size: number): void {
    this.pageSize = size;
    this.currentPage = 1;
    this.loadContracts();
  }

  private filterNearExpiration(data: Contract[]): Contract[] {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    return data.filter(c => {
      const statusUpper = c.status?.toUpperCase();
      
      if (statusUpper === 'EXPIRED' || statusUpper === 'EXPIRADO' || 
          statusUpper === 'CANCELLED' || statusUpper === 'CANCELADO') {
        return false;
      }
      
      if (!c.endDate) return false;
      
      const endDate = new Date(c.endDate);
      endDate.setHours(0, 0, 0, 0);
      const diffDays = Math.ceil((endDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
      
      return diffDays > 0 && diffDays < 3;
    });
  }

  private transformContractsData(data: Contract[]): Contract[] {
    return data.map(contract => ({
      ...contract,
      driverName: contract.driver?.name || 'N/A',
      driverCpfCnpj: contract.driver?.cpfCnpj || 'N/A',
      vehicleInfo: contract.vehicleSnapshot?.plate || 'N/A',
      period: `${this.formatDate(contract.startDate)} até ${this.formatDate(contract.endDate)}`,
      statusLabel: this.getStatusLabel(contract.status),
      baseAmount: contract.baseAmount || 0,
      _actions: this.buildContractRowDropdownActions(contract.status)
    }));
  }

  /** Congelar e Descongelar são mutuamente exclusivos no menu. */
  private buildContractRowDropdownActions(status: string | undefined): TableAction[] {
    const actions: TableAction[] = [
      { label: 'Renovar/migrar contrato', icon: 'fa-solid fa-rotate', action: 'renew', visible: false }
    ];
    if (statusAllowsContractFreeze(status)) {
      actions.push({
        label: 'Congelar',
        icon: 'fa-solid fa-snowflake',
        action: 'freeze',
        visible: false
      });
    } else if (statusAllowsContractUnfreeze(status)) {
      actions.push({
        label: 'Descongelar',
        icon: 'fa-solid fa-sun',
        action: 'unfreeze',
        visible: false
      });
    }
    actions.push(
      { label: 'Cancelar Contrato', icon: 'fa-solid fa-ban', action: 'cancel', visible: false },
      { label: 'Substituir Veículo', icon: 'fa-solid fa-car-side', action: 'replaceVehicle', visible: false }
    );
    return actions;
  }

  onTableAction(event: { action: string; item: any }): void {
    const contract = event.item as Contract;
    
    switch (event.action) {
      case 'view':
        this.navigateToViewContract(contract);
        break;
      case 'edit':
        this.navigateToEditContract(contract);
        break;
      case 'download':
        this.downloadContract(contract);
        break;
      case 'delete':
        this.deleteContract(contract);
        break;
      case 'renew':
        this.renewContract(contract);
        break;
      case 'cancel':
        this.cancelContract(contract);
        break;
      case 'replaceVehicle':
        this.replaceVehicle(contract);
        break;
      case 'freeze':
        this.openFreezeModal(contract);
        break;
      case 'unfreeze':
        this.confirmUnfreezeContract(contract);
        break;
    }
  }

  confirmUnfreezeContract(contract: Contract): void {
    const st = contract.status as string | undefined;
    if (!statusAllowsContractUnfreeze(st)) {
      this.messageService.showErrorMessage('Apenas contratos em estado congelado podem ser descongelados.');
      return;
    }
    this.confirmationService
      .confirm({
        title: 'Descongelar contrato',
        message:
          'Deseja descongelar este contrato agora (antes do fim do período de congelamento)?',
        confirmText: 'Descongelar',
        cancelText: 'Cancelar',
        type: 'warning'
      })
      .subscribe(confirmed => {
        if (!confirmed) return;
        this.isLoading = true;
        this.contractsService.unfreezeContract(contract.id).subscribe({
          next: (res) => {
            this.messageService.showInfoMessage(res.message || 'Contrato descongelado com sucesso.');
            this.loadContracts();
            this.loadDashboard();
            this.isLoading = false;
          },
          error: () => {
            this.isLoading = false;
          }
        });
      });
  }

  openFreezeModal(contract: Contract): void {
    const st = contract.status as string | undefined;
    if (!statusAllowsContractFreeze(st)) {
      this.messageService.showErrorMessage(
        'Só é possível congelar contratos vigentes, próximos ao vencimento ou aguardando pagamento.'
      );
      return;
    }
    this.contractFreezeId = contract.id;
    this.contractFreezeSubtitle = contract.contractNumber || `#${contract.id}`;
    this.showFreezeModal = true;
  }

  closeFreezeModal(): void {
    this.showFreezeModal = false;
    this.contractFreezeId = null;
    this.contractFreezeSubtitle = '';
  }

  onContractFrozen(res: ContractFreezeResponse): void {
    const msg =
      res?.message ||
      (res?.freezeStartUtc && res?.freezeEndUtc
        ? `Congelamento registrado (${res.status || 'Congelado'}).`
        : 'Contrato congelado com sucesso.');
    this.messageService.showInfoMessage(msg);
    this.closeFreezeModal();
    this.loadContracts();
    this.loadDashboard();
  }

  renewContract(contract: Contract): void {
    this.router.navigate(['/contracts', contract.id, 'renew']);
  }

  cancelContract(contract: Contract): void {
    this.contractToCancel = contract;
    this.cancelReasonCode = '';
    this.setCancelModalOpen(true);
  }

  closeCancelModal(): void {
    this.contractToCancel = null;
    this.cancelReasonCode = '';
    this.setCancelModalOpen(false);
  }

  getContractNumber(): string {
    return this.contractToCancel?.id?.toString().padStart(4, '0') || '';
  }

  confirmCancelContract(): void {
    if (!this.contractToCancel) return;

    const motivo = contractCancelMotivoFromCode(this.cancelReasonCode);
    if (!motivo) {
      this.messageService.showErrorMessage('Selecione o motivo do cancelamento.');
      return;
    }

    this.isLoading = true;
    this.contractsService.cancelContract(this.contractToCancel.id, { motivo }).subscribe({
      next: (response) => {
        const baseMessage = response.message || 'Contrato cancelado com sucesso!';

        // Depois de cancelar o contrato, buscamos o cliente Asaas pelo CPF/CNPJ
        this.contractsService.getContractComplete(this.contractToCancel!.id).subscribe({
          next: (contractData: any) => {
            const customerDoc = contractData.customer?.documentNumber || '';
            const driverDoc = contractData.contract?.driver?.cpfCnpj || contractData.driver?.cpfCnpj || '';
            const rawDoc = (customerDoc || driverDoc || '').toString();
            const digits = rawDoc.replace(/\D/g, '');

            const finish = (msg: string) => {
              this.messageService.showInfoMessage(msg);
              this.loadContracts();
              this.loadDashboard();
              this.closeCancelModal();
              this.isLoading = false;
            };

            if (!digits || (digits.length !== 11 && digits.length !== 14)) {
              finish(baseMessage);
              return;
            }

            this.paymentService.cancelPendingAsaasChargesAfterContractCancel(digits).subscribe({
              next: ({ hasOverdue, customerFound }) => {
                if (!customerFound) {
                  finish(baseMessage + ' (cliente Asaas não encontrado).');
                  return;
                }
                if (hasOverdue) {
                  const idx = this.contracts.findIndex(c => c.id === this.contractToCancel!.id);
                  if (idx !== -1) {
                    this.contracts[idx].status = 'Cancelled';
                    (this.contracts[idx] as any).statusLabel = 'Cancelado / Pend. Fin.';
                  }
                  finish(baseMessage + ' Contrato cancelado com pendência financeira (boletos vencidos mantidos no Asaas).');
                } else {
                  finish(baseMessage + ' Boletos pendentes cancelados no Asaas.');
                }
              },
              error: (err3) => {
                this.messageService.showErrorMessage(
                  `Contrato cancelado, mas houve erro ao processar cobranças no Asaas: ${err3?.message || err3}`
                );
                this.loadContracts();
                this.loadDashboard();
                this.closeCancelModal();
                this.isLoading = false;
              }
            });
          },
          error: () => {
            // Se não conseguir carregar o contrato completo, pelo menos manter o cancelamento
            this.messageService.showInfoMessage(baseMessage);
            this.loadContracts();
            this.loadDashboard();
            this.closeCancelModal();
            this.isLoading = false;
          }
        });
      },
      error: (err) => {
        this.messageService.showErrorMessage(`Erro ao cancelar contrato: ${err.message}`);
        this.isLoading = false;
      }
    });
  }

  replaceVehicle(contract: Contract): void {
    this.router.navigate(['/contracts', contract.id, 'replace-vehicle']);
  }

  onRowClick(contract: Contract): void {
    this.navigateToViewContract(contract);
  }

  navigateToViewContract(contract: Contract): void {
    this.router.navigate(['/contracts', contract.id, 'view']);
  }

  navigateToEditContract(contract: Contract): void {
    this.router.navigate(['/contracts', contract.id, 'edit']);
  }

  navigateToNewContract(): void {
    this.router.navigate(['/contracts/new']);
  }

  deleteContract(contract: Contract): void {
    if (confirm('Tem certeza que deseja excluir este contrato?')) {
      this.contractsService.deleteContract(contract.id).subscribe({
        next: () => {
          this.messageService.showInfoMessage('Contrato excluído com sucesso!');
          this.loadContracts();
          this.loadDashboard();
        },
        error: (err) => {
          this.messageService.showErrorMessage(`Erro ao excluir contrato: ${err.message}`);
        }
      });
    }
  }

  downloadContract(contract: Contract): void {
    this.contractsService.downloadContractDocument(contract.id).subscribe({
      next: (response) => {
        const url = window.URL.createObjectURL(response.blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = response.fileName;
        link.click();
        window.URL.revokeObjectURL(url);
        this.messageService.showInfoMessage('Download iniciado!');
      },
      error: (err) => {
        this.messageService.showErrorMessage(`Erro ao baixar contrato: ${err.message}`);
      }
    });
  }

  /** Retorna a contagem do dashboard para um status (ex.: "Vigente", "Vencido"). */
  private getDashboardCountByStatus(status: string): number {
    if (!this.dashboard?.countsByStatus?.length) return 0;
    const item = this.dashboard.countsByStatus.find(
      x => x.status?.toLowerCase() === status?.toLowerCase()
    );
    return item?.count ?? 0;
  }

  get totalContractsCount(): number {
    return this.dashboard?.totalCount ?? this.totalCount;
  }

  /** Contratos ativos / vigentes (Vigente + Assinado). */
  get activeContractsCount(): number {
    return this.getDashboardCountByStatus('Vigente') + this.getDashboardCountByStatus('Assinado');
  }

  /** Pendentes: Aguardando Assinatura + Draft + ReadyToGenerate. */
  get pendingContractsCount(): number {
    return (
      this.getDashboardCountByStatus('Aguardando Assinatura') +
      this.getDashboardCountByStatus('Draft') +
      this.getDashboardCountByStatus('ReadyToGenerate')
    );
  }

  /** Próximos ao vencimento (se o backend enviar esse status no dashboard). */
  get nearExpirationContractsCount(): number {
    return (
      this.getDashboardCountByStatus('Próximos ao Vencimento') +
      this.getDashboardCountByStatus('Proximos ao Vencimento')
    );
  }

  /** Expirados / vencidos. */
  get expiredContractsCount(): number {
    return this.getDashboardCountByStatus('Vencido');
  }

  formatDate(dateString: string): string {
    return new Date(dateString).toLocaleDateString('pt-BR');
  }

  getStatusLabel(status: string): string {
    const statusMap: Record<string, string> = {
      // Tanto Draft quanto Pending representam contrato aguardando assinatura
      'Draft': 'Pend. Assin.',
      'Pending': 'Pend. Assin.',
      'Signed': 'Assinado',
      'Active': 'Ativo',
      'Expired': 'Expirado',
      'Cancelled': 'Cancelado'
    };
    return statusMap[status] || status;
  }

  onSearchChange(): void {
    if (this.searchDebounce) clearTimeout(this.searchDebounce);
    this.searchDebounce = setTimeout(() => {
      this.currentPage = 1;
      this.loadContracts();
    }, 300);
  }

  onStatusChange(): void {
    this.currentPage = 1;
    this.loadContracts();
  }

  onSortChange(event: { sortBy: string; sortOrder: 'asc' | 'desc' }): void {
    this.sortBy = event.sortBy;
    this.sortOrder = event.sortOrder;
    this.contracts = this.sortData(this.contracts, event.sortBy, event.sortOrder);
  }

  exportToCSV(): void {
    const headers = this.tableColumns.map(c => c.titulo);
    const keys = this.tableColumns.map(c => c.label);

    const rows = this.contracts.map(contract =>
      keys.map(key => {
        const val = (contract as any)[key];
        if (val === null || val === undefined) return '';
        const str = String(val);
        return str.includes(',') || str.includes('"') || str.includes('\n')
          ? `"${str.replace(/"/g, '""')}"` : str;
      }).join(',')
    );

    const csv = [headers.join(','), ...rows].join('\n');
    const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `contratos_${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  }

  private sortData<T>(data: T[], sortBy: string, sortOrder: 'asc' | 'desc'): T[] {
    if (!sortBy) return data;
    return [...data].sort((a, b) => {
      const va = (a as any)[sortBy] ?? '';
      const vb = (b as any)[sortBy] ?? '';
      const cmp = String(va).localeCompare(String(vb), 'pt-BR', { numeric: true });
      return sortOrder === 'asc' ? cmp : -cmp;
    });
  }
}
